/**
 * AdminFounderIntegrityCard — Registry integrity audit + repair wizard.
 *
 * Emergency tool for repairing the `founder_positions` registry when it
 * falls out of the 100-slot invariant. Guides the admin through:
 *
 *   1. Audit           — read-only corruption report
 *   2. Snapshot        — timestamped backup of positions + grants
 *   3. Resolve         — pick a keeper for any position with 2+ ASSIGNED
 *   4. Repair          — dry-run plan → commit → verify healthy
 *
 * Nothing here mutates data until the admin explicitly commits. Every
 * action gets an audit_log entry in `founder_grants`.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, AlertTriangle, Camera, Wrench, Loader2,
  RefreshCw, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";

// Note: build the endpoint URLs INSIDE each function, never at module scope.
// `API` is exported from @/App and reading it during module evaluation
// creates a TDZ circular-import failure that crashes the entire app in
// production (blank white page).

function StatChip({ label, value, tone = "navy" }) {
  const tones = {
    navy: "bg-tbc-navy text-white",
    gold: "bg-tbc-gold text-tbc-navy",
    warn: "bg-amber-50 text-amber-900 border border-amber-200",
    danger: "bg-rose-50 text-rose-800 border border-rose-200",
    ok: "bg-emerald-50 text-emerald-800 border border-emerald-200",
  };
  return (
    <div className={`px-3 py-2 rounded-lg ${tones[tone]}`} data-testid={`integrity-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-75">{label}</div>
      <div className="font-heading text-2xl leading-none mt-0.5">{value}</div>
    </div>
  );
}

export default function AdminFounderIntegrityCard() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(""); // "snapshot" | "dryrun" | "commit"
  const [snapshots, setSnapshots] = useState([]); // list of snapshot IDs created this session
  const [resolutions, setResolutions] = useState({}); // { [position]: keep_business_id }
  const [plan, setPlan] = useState(null);

  const loadAudit = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/founder/admin/integrity/audit`, { withCredentials: true });
      setAudit(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  const runSnapshot = async () => {
    setBusy("snapshot");
    try {
      const r = await axios.post(`${API}/founder/admin/integrity/snapshot`, {}, { withCredentials: true });
      setSnapshots((prev) => [r.data, ...prev]);
      toast.success(`Snapshot saved: ${r.data.snapshot_id} (${r.data.positions_saved} positions, ${r.data.grants_saved} grants)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Snapshot failed");
    } finally {
      setBusy("");
    }
  };

  const buildResolutionsPayload = () =>
    Object.entries(resolutions)
      .filter(([, biz]) => !!biz)
      .map(([position, keep_business_id]) => ({
        position: Number(position),
        keep_business_id,
        displace_action: "unassign",
      }));

  const conflicts = audit?.conflicts_needs_admin_review || [];
  const unresolvedConflicts = conflicts.filter((c) => !resolutions[c.position]);

  const runDryRun = async () => {
    setBusy("dryrun");
    setPlan(null);
    try {
      const r = await axios.post(
        `${API}/founder/admin/integrity/repair`,
        { dry_run: true, resolutions: buildResolutionsPayload() },
        { withCredentials: true },
      );
      if (r.data.aborted) {
        toast.error(`Repair blocked: ${r.data.reason} (positions: ${r.data.unresolved_positions?.join(", ")})`);
        return;
      }
      setPlan(r.data.plan);
      toast.success(`Dry-run OK: ${r.data.plan.action_count} action(s) proposed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry-run failed");
    } finally {
      setBusy("");
    }
  };

  const commitRepair = async () => {
    if (!plan) {
      toast.error("Run dry-run first");
      return;
    }
    if (snapshots.length === 0) {
      const ok = window.confirm(
        "You haven't taken a snapshot this session. Continue anyway?\n\n" +
        "Strongly recommended: click 'Snapshot' first so the pre-repair state can be restored if needed.",
      );
      if (!ok) return;
    }
    if (!window.confirm(
      `Commit ${plan.action_count} repair action(s)?\n\n` +
      `This will delete/insert rows in founder_positions and may unassign businesses from contested slots. ` +
      `Every write is audited to founder_grants.`,
    )) return;
    setBusy("commit");
    try {
      const r = await axios.post(
        `${API}/founder/admin/integrity/repair`,
        { dry_run: false, resolutions: buildResolutionsPayload() },
        { withCredentials: true },
      );
      if (r.data.aborted) {
        toast.error(`Repair aborted: ${r.data.reason}`);
        return;
      }
      const s = r.data.summary;
      toast.success(`Repair committed — deleted ${s.deleted}, added ${s.added}, displaced ${s.displaced}, skipped ${s.skipped}`);
      setPlan(null);
      setResolutions({});
      setAudit(r.data.audit_after);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    } finally {
      setBusy("");
    }
  };

  const t = audit?.totals || {};
  const healthy = !!audit?.healthy;
  const sumOk = t.sum_matches_expected;

  return (
    <div className="rounded-2xl border border-tbc-navy/10 bg-white shadow-sm" data-testid="admin-founder-integrity-card">
      <div className="p-5 border-b border-tbc-navy/10 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-heading text-lg text-tbc-navy flex items-center gap-2">
            {healthy ? (
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
            ) : (
              <ShieldAlert className="w-4 h-4 text-rose-600" />
            )}
            Registry Integrity
          </h3>
          <p className="text-xs text-tbc-navy/60 mt-1">
            The Founding 100 must contain exactly 100 canonical rows with claimed + reserved + available = 100. This tool audits and repairs the registry if it drifts.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="rounded-full border-tbc-navy/20 text-tbc-navy hover:bg-tbc-navy hover:text-white"
            onClick={loadAudit}
            disabled={loading}
            data-testid="integrity-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Refresh
          </Button>
          <Button
            variant="outline"
            className="rounded-full border-tbc-navy/20 text-tbc-navy hover:bg-tbc-navy hover:text-white"
            onClick={runSnapshot}
            disabled={busy === "snapshot"}
            data-testid="integrity-snapshot"
          >
            {busy === "snapshot" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Camera className="w-4 h-4 mr-1.5" />}
            Snapshot
          </Button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {loading && !audit ? (
          <div className="py-6 flex items-center gap-2 text-tbc-navy/50 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading audit…
          </div>
        ) : audit ? (
          <>
            {/* Health banner */}
            <div
              className={`rounded-xl p-4 border-2 ${healthy ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"}`}
              data-testid="integrity-health-banner"
            >
              <div className={`flex items-center gap-2 font-heading text-lg ${healthy ? "text-emerald-800" : "text-rose-800"}`}>
                {healthy ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                {healthy ? "Registry is healthy" : "Registry is corrupted"}
              </div>
              <p className={`text-xs mt-1 ${healthy ? "text-emerald-700" : "text-rose-700"}`}>
                {healthy
                  ? "All 100 canonical positions are present, unique, and consistent."
                  : "Anomalies detected — review below, snapshot, resolve conflicts, then run repair."}
              </p>
            </div>

            {/* Counter grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <StatChip label="Rows" value={t.rows ?? "—"} tone={t.rows === 100 ? "navy" : "danger"} />
              <StatChip label="Assigned" value={t.assigned ?? "—"} tone="gold" />
              <StatChip label="Reserved" value={t.reserved ?? "—"} tone="navy" />
              <StatChip label="Available" value={t.available ?? "—"} tone="navy" />
              <StatChip label="Sum" value={t.sum ?? "—"} tone={sumOk ? "ok" : "danger"} />
            </div>

            {/* Anomalies detail */}
            {!healthy && (
              <div className="rounded-xl border border-tbc-navy/10 divide-y divide-tbc-navy/5 text-sm">
                <AnomalyRow
                  label="Duplicate positions"
                  value={audit.duplicate_positions?.length || 0}
                  detail={audit.duplicate_positions?.map((d) => `#${d.position} (${d.count}×)`).join(", ")}
                />
                <AnomalyRow
                  label="Out-of-range rows"
                  value={audit.out_of_range?.length || 0}
                  detail={audit.out_of_range?.map((r) => `#${r.position}`).join(", ")}
                />
                <AnomalyRow
                  label="Missing positions"
                  value={audit.missing_positions?.length || 0}
                  detail={audit.missing_positions?.slice(0, 10).map((n) => `#${n}`).join(", ") + (audit.missing_positions?.length > 10 ? `, +${audit.missing_positions.length - 10} more` : "")}
                />
                <AnomalyRow
                  label="Businesses in ≥2 positions"
                  value={audit.businesses_in_multiple_positions?.length || 0}
                />
                <AnomalyRow
                  label="Orphan slots (business gone)"
                  value={audit.orphan_business_ids?.length || 0}
                />
                <AnomalyRow
                  label="Tagged founders w/o slot"
                  value={audit.businesses_tagged_without_assigned_slot?.length || 0}
                />
              </div>
            )}

            {/* Conflicts — human resolution */}
            {conflicts.length > 0 && (
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="integrity-conflicts">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-700 flex-none mt-0.5" />
                  <div>
                    <p className="font-heading text-amber-900">
                      {conflicts.length} position conflict(s) — pick the keeper for each
                    </p>
                    <p className="text-xs text-amber-800 mt-1">
                      Each contested position has two or more legitimate businesses. Keep one at that number; the other is unassigned and can be granted a new position afterward.
                    </p>
                  </div>
                </div>
                {conflicts.map((c) => (
                  <div key={c.position} className="rounded-lg bg-white border border-amber-200 p-3">
                    <p className="font-heading text-tbc-navy">
                      Position <span className="text-tbc-gold">#{String(c.position).padStart(3, "0")}</span>
                    </p>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2">
                      {c.candidates.map((cand) => {
                        const picked = resolutions[c.position] === cand.business_id;
                        return (
                          <button
                            key={cand.business_id}
                            type="button"
                            onClick={() =>
                              setResolutions((prev) => ({ ...prev, [c.position]: cand.business_id }))
                            }
                            className={`text-left rounded-lg border-2 p-3 transition-colors ${
                              picked
                                ? "border-tbc-navy bg-tbc-cream"
                                : "border-tbc-navy/15 hover:border-tbc-navy/40 bg-white"
                            }`}
                            data-testid={`integrity-conflict-pick-${c.position}-${cand.business_id}`}
                          >
                            <div className="flex items-center gap-2">
                              {cand.logo_url ? (
                                <img src={cand.logo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-tbc-cream border border-tbc-navy/10" />
                              )}
                              <div className="min-w-0">
                                <p className="font-medium text-tbc-navy truncate">{cand.name}</p>
                                <p className="text-[10px] text-tbc-navy/50 font-mono truncate">{cand.business_id}</p>
                              </div>
                              {picked && <CheckCircle2 className="w-4 h-4 text-emerald-600 ml-auto" />}
                            </div>
                            <div className="mt-2 text-[10px] uppercase tracking-widest text-tbc-navy/50">
                              {cand.assigned_source || "—"} · {cand.assigned_at?.slice(0, 10) || "no date"}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Repair actions */}
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <Button
                onClick={runDryRun}
                disabled={busy === "dryrun" || (conflicts.length > 0 && unresolvedConflicts.length > 0)}
                className="rounded-full bg-tbc-navy text-white hover:bg-tbc-navy/90 disabled:opacity-40"
                data-testid="integrity-dryrun"
              >
                {busy === "dryrun" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Wrench className="w-4 h-4 mr-1.5" />}
                Dry-run repair
              </Button>
              {plan && (
                <Button
                  onClick={commitRepair}
                  disabled={busy === "commit"}
                  className="rounded-full bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90 font-semibold"
                  data-testid="integrity-commit"
                >
                  {busy === "commit" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1.5" />}
                  Commit {plan.action_count} action(s)
                </Button>
              )}
              {conflicts.length > 0 && unresolvedConflicts.length > 0 && (
                <p className="text-xs text-amber-700">
                  Resolve {unresolvedConflicts.length} conflict(s) above before running dry-run.
                </p>
              )}
            </div>

            {/* Plan preview */}
            {plan && (
              <div className="rounded-lg bg-tbc-navy/5 border border-tbc-navy/10 p-3 text-xs" data-testid="integrity-plan">
                <p className="font-semibold text-tbc-navy mb-2">
                  Proposed plan ({plan.action_count} action{plan.action_count === 1 ? "" : "s"})
                </p>
                <ul className="space-y-1 max-h-56 overflow-y-auto pr-2">
                  {plan.actions.map((a, i) => (
                    <li key={`${a.type}:${a.position ?? "np"}:${a.business_id ?? "nb"}:${i}`} className="font-mono text-tbc-navy/80">
                      <span className="text-tbc-navy/40">{i + 1}.</span> {a.type} · pos={a.position ?? "—"}
                      {a.business_id && ` · biz=${a.business_id.slice(0, 12)}…`}
                      {a.row_business_id && ` · row_biz=${a.row_business_id.slice(0, 12)}…`}
                      {a.row_status && ` · ${a.row_status}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Snapshot ledger */}
            {snapshots.length > 0 && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs">
                <p className="font-semibold text-emerald-900 mb-1">
                  Snapshots taken this session ({snapshots.length})
                </p>
                <ul className="space-y-0.5 text-emerald-800 font-mono">
                  {snapshots.map((s) => (
                    <li key={s.snapshot_id}>
                      {s.snapshot_id} — {s.positions_saved} positions, {s.grants_saved} grants
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-tbc-navy/50">Couldn't load audit.</p>
        )}
      </div>
    </div>
  );
}

function AnomalyRow({ label, value, detail }) {
  const bad = value > 0;
  return (
    <div className="px-3 py-2 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className={`font-medium ${bad ? "text-rose-800" : "text-tbc-navy/60"}`}>{label}</p>
        {detail && bad && <p className="text-[11px] text-tbc-navy/60 truncate">{detail}</p>}
      </div>
      <span className={`text-2xl font-heading ${bad ? "text-rose-600" : "text-tbc-navy/30"}`}>{value}</span>
    </div>
  );
}
